import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import MeetingDetailsStatusSidebar from "./MeetingDetailsStatusSidebar";
import MeetingDetailsGridPanel from "./MeetingDetailsGridPanel";
import { STATUS_TYPES } from "./meetingDetailsConstants";

/**
 * Sidebar + single active status panel (replaces accordion for consultations layout).
 */
export default function MeetingDetailsSidebarLayout({
  status,
  selectedType,
  onSelectStatus,
  sectionLoaders,
  meetingDatasources,
  gridRef,
  columnDefsMap,
  defaultColDef,
  cellClickedListener,
  onGridReady,
  rowClassRules,
  onSelectionChanged,
  onRefreshSection,
  onSortChanged,
  onFilterChanged,
}) {
  const { t } = useTranslation();

  const sectionIndex = useMemo(
    () => STATUS_TYPES.indexOf(selectedType),
    [selectedType]
  );

  const section = status.find((s) => s.type === selectedType);
  const isLoading = sectionLoaders[selectedType] || false;

  if (!section || sectionIndex < 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "flex-start" },
        gap: { xs: 2, md: 2.5 },
        mt: 1,
        minHeight: { md: "min(72vh, 760px)" },
      }}
    >
      <MeetingDetailsStatusSidebar
        status={status}
        selectedType={selectedType}
        onSelect={onSelectStatus}
      />

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minWidth: 0,
          borderRadius: 3,
          overflow: "hidden",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)",
          border: "1px solid rgba(148,163,184,0.18)",
          boxShadow: "0 4px 32px rgba(15,23,42,0.08)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: "1px solid rgba(148,163,184,0.15)",
            background:
              "linear-gradient(90deg, rgba(241,245,249,0.9) 0%, rgba(255,255,255,0.6) 100%)",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "#0f172a",
            }}
          >
            {section.type}
            {section.totalCount > 0 && (
              <Typography
                component="span"
                variant="body2"
                sx={{ ml: 1, color: "text.secondary", fontWeight: 500 }}
              >
                ({section.totalCount})
              </Typography>
            )}
          </Typography>
          <Tooltip title={t("Refresh")}>
            <span>
              <IconButton
                size="small"
                disabled={isLoading}
                onClick={() => onRefreshSection(selectedType)}
                sx={{
                  border: "1px solid rgba(148,163,184,0.35)",
                  "&:hover": {
                    background: "rgba(56,189,248,0.08)",
                    borderColor: "rgba(56,189,248,0.45)",
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box
          key={selectedType}
          sx={{
            px: { xs: 1, sm: 2 },
            py: 2,
            animation: "consultPanelIn 0.4s cubic-bezier(0.33, 1, 0.68, 1) both",
            "@keyframes consultPanelIn": {
              "0%": {
                opacity: 0,
                transform: "translateX(12px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateX(0)",
              },
            },
          }}
        >
          <MeetingDetailsGridPanel
            section={section}
            sectionIndex={sectionIndex}
            isLoading={isLoading}
            datasource={meetingDatasources[selectedType]}
            columnDefs={columnDefsMap[selectedType]}
            defaultColDef={defaultColDef}
            cellClickedListener={cellClickedListener}
            onGridReady={onGridReady}
            rowClassRules={rowClassRules}
            onSelectionChanged={onSelectionChanged}
            onSortChanged={onSortChanged}
            onFilterChanged={onFilterChanged}
            gridRef={gridRef}
            gridHeight="min(62vh, 620px)"
          />
        </Box>
      </Paper>
    </Box>
  );
}
